import i18next from "i18next";
import { AppConfig } from "@/common/appConfig";
import { TranscriptionProviderName } from "@/hooks/transcriber/types";
import { useAuthStore } from "@/store/auth.store";
import { useSettingsOptions } from "@/store/settingsOptions.store";

// Transcriber input codes that differ from the stored note language codes
// (see noteLanguageForInput in note-language.ts for the other direction).
const INPUT_CODES = new Set(["eng", "spa", "fra", "por", "jap", "cmn"]);

const inputCodeForNoteLanguage = (): string | undefined => {
  const id =
    useAuthStore.getState().userData?.userPreference?.transcriptionLanguageId;
  if (id === undefined || id === null) return undefined;
  const language = useSettingsOptions
    .getState()
    .transcriptionLanguages?.find((entry) => entry.id === id);
  if (!language) return undefined;
  if (language.name === "Mandarin") return "cmn";
  const code = language.code === "jpn" ? "jap" : (language.code ?? "");
  return INPUT_CODES.has(code) ? code : undefined;
};

// Interface language (BCP 47 primary subtag) -> transcriber input code.
const UI_INPUT_CODES: Record<string, string> = {
  en: "eng",
  es: "spa",
  fr: "fra",
  pt: "por",
  ja: "jap",
  zh: "cmn",
};

// The conversation (input) language that matches an interface language:
// "pt-BR" -> "por". Undefined for interface languages ROSC cannot transcribe.
export const inputCodeForInterface = (
  interfaceLanguage: string | null | undefined,
): string | undefined => {
  const language = (interfaceLanguage ?? "").split("-")[0]?.toLowerCase();
  return language ? UI_INPUT_CODES[language] : undefined;
};

const inputCodeForInterfaceLanguage = (): string | undefined =>
  inputCodeForInterface(i18next.resolvedLanguage ?? i18next.language);

/**
 * The language a dictation is transcribed in: the field's own language when
 * it has one, else the account's note language, else the interface
 * language, else automatic detection.
 *
 * Deliberately not the visit recorder's Input Language: that follows the
 * last visit (after a Japanese visit, English dictation came out in
 * Japanese), while a clinician dictates notes and instructions in their own
 * working language.
 */
export const resolveDictationLanguage = (override?: string): string => {
  if (override && override !== "auto") return override;
  return (
    inputCodeForNoteLanguage() ?? inputCodeForInterfaceLanguage() ?? "auto"
  );
};

/**
 * Realtime provider for a dictation in `language` (an input code). English
 * goes to Deepgram (nova-3-medical, as English live recording) when
 * VITE_DICTATION_DEEPGRAM_EN_ENABLED is on; everything else stays on the
 * first configured realtime provider.
 */
export const dictationProviderFor = (
  language: string,
): TranscriptionProviderName => {
  if (AppConfig.enableDictationDeepgramEn && language === "eng") {
    return TranscriptionProviderName.DEEPGRAM;
  }
  return AppConfig.transcriptionsProvidersRealtime[0];
};
